import React, { useState, useMemo } from 'react';
import { getAllCountries } from '../lib/plateFormats';
import { Search, MapPin, X } from 'lucide-react';

/**
 * Full-screen region selector shown on first launch.
 * Lets the user pick their country so we can show the correct plate format hints.
 */
const RegionSelector = ({ onSelect, language = 'en' }) => {
  const [search, setSearch] = useState('');
  const countries = useMemo(() => getAllCountries(), []);

  const filtered = useMemo(() => {
    if (!search.trim()) return countries;
    const q = search.toLowerCase();
    return countries.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [search, countries]);

  const labels = {
    en: { title: 'Select Your Region', subtitle: 'This helps us show the correct license plate format', search: 'Search countries...' },
    bg: { title: 'Изберете вашия регион', subtitle: 'Това помага да покажем правилния формат на регистрационния номер', search: 'Търси държави...' },
  };
  const t = labels[language] || labels.en;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'var(--bg-primary, #0f172a)',
      display: 'flex', flexDirection: 'column',
      color: 'var(--text-primary, white)',
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center', padding: '2.5rem 1.5rem 1rem',
        background: '#738ADB',
        color: 'white',
        borderBottomLeftRadius: '24px',
        borderBottomRightRadius: '24px',
        boxShadow: '0 8px 32px rgba(79, 70, 229, 0.3)',
      }}>
        <div style={{
          width: '64px', height: '64px', margin: '0 auto 1rem',
          background: 'rgba(255,255,255,0.15)', borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(10px)',
        }}>
          <MapPin size={32} color="white" />
        </div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0 0 0.5rem', letterSpacing: '-0.5px' }}>
          {t.title}
        </h1>
        <p style={{ fontSize: '0.9rem', opacity: 0.8, margin: 0, lineHeight: 1.4 }}>
          {t.subtitle}
        </p>
      </div>

      {/* Search */}
      <div style={{ padding: '1rem 1rem 0.5rem', position: 'relative' }}>
        <Search size={18} style={{
          position: 'absolute', left: '1.75rem', top: '50%',
          transform: 'translateY(-50%)', color: 'var(--text-secondary, #9ca3af)', pointerEvents: 'none'
        }} />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t.search}
          autoFocus
          style={{
            width: '100%', padding: '0.85rem 0.85rem 0.85rem 2.75rem',
            border: '2px solid var(--border-color, #334155)',
            borderRadius: '14px', background: 'var(--bg-secondary, #1e293b)',
            color: 'var(--text-primary, white)', fontSize: '1rem',
            outline: 'none', boxSizing: 'border-box',
            transition: 'border-color 0.2s',
          }}
          onFocus={e => e.target.style.borderColor = '#738ADB'}
          onBlur={e => e.target.style.borderColor = 'var(--border-color, #334155)'}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{
              position: 'absolute', right: '1.75rem', top: '50%',
              transform: 'translateY(-50%)', background: 'none', border: 'none',
              cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px',
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Country List */}
      <div style={{
        flex: 1, overflow: 'auto', padding: '0.5rem 1rem 2rem',
        WebkitOverflowScrolling: 'touch',
      }}>
        {filtered.map(country => (
          <button
            key={country.code}
            onClick={() => onSelect(country.code)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              width: '100%', padding: '0.85rem 1rem',
              background: 'var(--bg-secondary, #1e293b)',
              border: '1px solid var(--border-color, #334155)',
              borderRadius: '12px', marginBottom: '0.5rem',
              cursor: 'pointer', color: 'var(--text-primary, white)',
              transition: 'all 0.15s', textAlign: 'left',
              fontSize: '1rem',
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = 'rgba(99, 102, 241, 0.15)';
              e.currentTarget.style.borderColor = '#738ADB';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = 'var(--bg-secondary, #1e293b)';
              e.currentTarget.style.borderColor = 'var(--border-color, #334155)';
            }}
          >
            <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{country.flag}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{country.name}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #9ca3af)', marginTop: '2px' }}>
                Plate: {country.placeholder}
              </div>
            </div>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500, opacity: 0.6 }}>
              {country.code}
            </span>
          </button>
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-secondary)' }}>
            <p style={{ fontSize: '1.1rem', margin: '0 0 0.5rem' }}>No countries found</p>
            <p style={{ fontSize: '0.85rem', margin: 0 }}>Try a different search term</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegionSelector;
